import { useState } from 'react';
import { Button, Switch, Divider, Popup, InputNumber, Cell, Row, Col } from '@nutui/nutui-react';
import { formatOpenTime } from '../common';

// 与 Setting.js 同级单元格保持一致的容器样式
const cellStyle = {
    flexWrap: 'wrap',
    borderRadius: '6px',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexDirection: 'row',
    display: 'flex',
};

/**
 * 威震逍遥津-活动道具兑换设置。
 * 覆盖多处兑换（玄武兑换 actType=2、玄武宝库 actType=34 等）：
 * 商品来自 /v1/config/weiZhenExchange（按活动分组的 ExchangeActConf），
 * 配置写入 formValues.activity.weiZhenExchange（{ open, targets:[{goodsId,count}] }）。
 * 后端 ExchangeActivity.exchangeGroupedByTargets 会按 goodsId 归组到各活动分别兑换。
 */
function WeiZhenExchangeView({ weiZhenConfig, loading, exchangeConfig, user, onFormChange }) {
    const [pickerVisible, setPickerVisible] = useState(false);

    const activities = weiZhenConfig?.activities ?? [];
    const targets = exchangeConfig?.targets ?? [];
    const open = exchangeConfig?.open === true;

    // 跨活动按 goodsId 反查商品配置及其所属活动（含消耗道具名）
    const findGoods = (goodsId) => {
        for (const group of activities) {
            const conf = group.exchangeList?.find((c) => c.id === goodsId);
            if (conf) {
                return { conf, activity: group.activity };
            }
        }
        return null;
    };

    return (
        <Cell style={cellStyle}>
            {/* 标题 + 总开关 */}
            <Row type="flex" wrap="nowrap" justify="center" align="center">
                <Col span="16">威震逍遥津-兑换奖励道具</Col>
                <Col span="8" style={{ textAlign: 'right' }}>
                    <Switch
                        style={{ float: 'right' }}
                        checked={open}
                        onChange={(value) => onFormChange('activity:weiZhenExchange:open', value)}
                    />
                </Col>
            </Row>

            {open && (
                <>
                    {activities.length === 0 && (
                        <Row>
                            <Col span="24">
                                <div style={{ textAlign: 'center', padding: '12px', color: '#999' }}>
                                    {loading ? '加载中...' : '当前没有开放的兑换活动'}
                                </div>
                            </Col>
                        </Row>
                    )}

                    {activities.length > 0 && (
                        <>
                            {/* 各活动消耗道具现状 */}
                            <Divider />
                            <Row>
                                {activities.map((group) => (
                                    <Col
                                        key={group.activity.id}
                                        span={activities.length >= 2 ? '12' : '24'}
                                        style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}
                                    >
                                        {group.activity.name}｜现有{group.activity.itemName}：
                                        {user?.role_info?.items?.[group.activity.itemName] ?? '0'}
                                        {group.taskStartTime ? `（${formatOpenTime(group.taskStartTime)}后开放）` : ''}
                                    </Col>
                                ))}
                            </Row>
                        </>
                    )}

                    {/* 已配置的兑换目标 + 添加按钮，统一放整宽 Col 内，避免 flex 容器横向乱排 */}
                    <Divider />
                    <Row>
                        <Col span="24">
                            <div style={{ marginBottom: '8px' }}>
                                兑换目标列表（按由上到下顺序，各活动独立按限购/库存/解锁校验）：
                            </div>
                            <Row type="flex" wrap="nowrap" align="center">
                                <Col span="10">道具名称</Col>
                                <Col span="7">兑换次数</Col>
                                <Col span="7">操作</Col>
                            </Row>
                            {targets.map((target, index) => {
                                const found = findGoods(target.goodsId);
                                if (!found) return null;
                                const { conf, activity } = found;
                                return (
                                    <div
                                        key={index}
                                        style={{
                                            border: '1px solid #eee',
                                            borderRadius: '8px',
                                            padding: '10px',
                                            marginBottom: '10px',
                                            backgroundColor: '#f9f9f9',
                                        }}
                                    >
                                        <Row type="flex" wrap="nowrap" align="center">
                                            <Col span="10">
                                                <div style={{ fontWeight: 'bold' }}>{conf.rewardName}</div>
                                                <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                                                    {activity.name}｜需要{conf.consumeNum}个{activity.itemName} | 限
                                                    {conf.limitNum}次
                                                    {conf.unlockNeed > 0 && `（累计消耗${conf.unlockNeed}个解锁）`}
                                                </div>
                                            </Col>
                                            <Col span="7">
                                                <InputNumber
                                                    value={target.count}
                                                    min={1}
                                                    max={conf.limitNum}
                                                    onChange={(value) =>
                                                        onFormChange(
                                                            `activity:weiZhenExchange:count:${index}`,
                                                            parseInt(value) || 1
                                                        )
                                                    }
                                                    style={{ width: '90px' }}
                                                />
                                            </Col>
                                            <Col span="7">
                                                <Button
                                                    type="default"
                                                    size="mini"
                                                    disabled={index === 0}
                                                    onClick={() => onFormChange(`activity:weiZhenExchange:moveUp:${index}`, null)}
                                                    style={{ marginRight: '5px' }}
                                                >
                                                    ↑
                                                </Button>
                                                <Button
                                                    type="default"
                                                    size="mini"
                                                    disabled={index === targets.length - 1}
                                                    onClick={() => onFormChange(`activity:weiZhenExchange:moveDown:${index}`, null)}
                                                    style={{ marginRight: '5px' }}
                                                >
                                                    ↓
                                                </Button>
                                                <Button
                                                    type="danger"
                                                    size="mini"
                                                    onClick={() => onFormChange(`activity:weiZhenExchange:delete:${index}`, null)}
                                                >
                                                    X
                                                </Button>
                                            </Col>
                                        </Row>
                                    </div>
                                );
                            })}
                            <Button type="info" size="normal" block onClick={() => setPickerVisible(true)} style={{ marginTop: '10px' }}>
                                + 添加兑换目标
                            </Button>
                        </Col>
                    </Row>

                    {/* 选择商品弹窗：按活动分组 */}
                    <Popup
                        closeable
                        title="选择兑换道具"
                        visible={pickerVisible}
                        style={{ height: '60%' }}
                        position="bottom"
                        onClose={() => setPickerVisible(false)}
                    >
                        <div style={{ padding: '10px', maxHeight: 'calc(60vh - 60px)', overflowY: 'auto' }}>
                            {loading ? (
                                <div style={{ textAlign: 'center', padding: '20px' }}>加载中...</div>
                            ) : activities.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>暂无兑换配置</div>
                            ) : (
                                activities.map((group) => (
                                    <div key={group.activity.id} style={{ marginBottom: '16px' }}>
                                        <div style={{ fontWeight: 'bold', margin: '8px 0' }}>
                                            {group.activity.name}（消耗：{group.activity.itemName}）
                                        </div>
                                        {group.exchangeList.map((conf) => {
                                            const added = targets.some((t) => t.goodsId === conf.id);
                                            return (
                                                <div
                                                    key={conf.id}
                                                    style={{
                                                        marginBottom: '10px',
                                                        padding: '12px',
                                                        border: '1px solid #e8e8e8',
                                                        borderRadius: '8px',
                                                        backgroundColor: added ? '#f0f0f0' : '#fafafa',
                                                        opacity: added ? 0.6 : 1,
                                                        cursor: added ? 'not-allowed' : 'pointer',
                                                    }}
                                                    onClick={() => {
                                                        if (!added) {
                                                            onFormChange('activity:weiZhenExchange:addTarget', parseInt(conf.id));
                                                        }
                                                    }}
                                                >
                                                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                                        {conf.rewardName}
                                                        {added && (
                                                            <span style={{ color: '#999', marginLeft: '8px' }}>(已添加)</span>
                                                        )}
                                                    </div>
                                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                                        需要{conf.consumeNum}个{group.activity.itemName} | 限{conf.limitNum}次
                                                        {conf.unlockNeed > 0 && `（累计消耗${conf.unlockNeed}个解锁）`}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ))
                            )}
                        </div>
                    </Popup>
                </>
            )}
        </Cell>
    );
}

export default WeiZhenExchangeView;
